import './style.scss';
import { Add, Block, DateRange, FilterAlt, People, Refresh, Today, Search as SearchIcon } from "@mui/icons-material";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { useEffect, useCallback, useMemo } from "react";
import FormHelper from "../../../../helpers/FormHelper";
import AddDialog from "./AddDialog";
import { useAuthContext } from "../../../../contexts/AuthContext";
import { INavigationLink } from "../../../../interfaces/INavigation";
import Filters from "./Filters";
import Tooltip from "../../../../components/Tooltip";
import useDrawer from "../../../../hooks/useDrawer";
import useDialog from "../../../../hooks/useDialog";
import PartnerPmsService from "../../../../services/PartnerPmsService";
import { IRoomBlocking } from "../../../../interfaces/IPartnerPms";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";
import useState from "../../../../hooks/useState";
import StringHelper from "../../../../helpers/StringHelper";
import useSearch from "../../../../hooks/useSearch";
import DefaultConstants from "../../../../data/Constants";
import Empty from "../../../../components/Empty";
import Search from "../../../../components/Search";
import RegistrationStatus from '../../../../components/RegistrationStatus';
import ViewDialog from './ViewDialog';


interface IStateValues {
    filters: FormData | undefined;
    data: IRoomBlocking[];
    selectedRoomBlocking: IRoomBlocking | undefined;
}

const RoomBlocking = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { hasPermission } = useAuthContext();

    const state = useState<IStateValues>({
        filters: undefined,
        data: [],
        selectedRoomBlocking: undefined
    });

    const filtersDrawer = useDrawer();
    const addDialog = useDialog();
    const viewDialog = useDialog();
    
    const search = useSearch();


    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, [state.values.filters]);
    
    
    const handleRefresh = useCallback(() => {
        let formData = state.values.filters;
        
        if (formData === undefined) {
            formData = FormHelper.Create(undefined);

            const now = DateTimeHelper.Now();
            formData = FormHelper.Add(formData, 'startDate', now);
            formData = FormHelper.Add(formData, 'endDate', now);
        }

        PartnerPmsService.ListRoomBlocking(formData)
            .then((resp) => {
                state.update('data', resp.data.roomBlocking as IRoomBlocking[]);
            });
        
        // eslint-disable-next-line
    }, [state.values.filters]);


    const handleAddOrView = useCallback((row: IRoomBlocking | undefined) => {
        state.update('selectedRoomBlocking', row);

        if (row !== undefined) {
            viewDialog.handleOpen();
        } else {
            addDialog.handleOpen();
        }
        // eslint-disable-next-line
    }, []);


    const roomBlockings = useMemo(() => {
        let roomBlockingsAux = state.values.data;
    
        if (search.value.trim().length > 0) {
            const value = StringHelper.Normalize(search.value.toUpperCase());
            roomBlockingsAux = roomBlockingsAux.filter(rb => 
                StringHelper.Normalize(rb.roomDescription.toUpperCase()).includes(value) 
                || StringHelper.Normalize(rb.typeDescription.toUpperCase()).includes(value)
            );
        }
            
        return roomBlockingsAux.map(rb => {
            return <Grid item lg={3} md={6} sm={12} xs={12} key={rb.groupId}>
                <Box className='roomblocking-card' onClick={() => handleAddOrView(rb)}>
                    <Box className='box-room'>
                        <Typography variant='subtitle1' className='text-title'> {rb.roomDescription} </Typography>
                        <RegistrationStatus id={rb.registrationStatusId} size='sm' />
                    </Box>

                    <Box className='box-informations'>
                        <Box className='box-information'>
                            <DateRange />
                            <Typography variant='subtitle2'>
                                {
                                    translate('startDateToEndDate')
                                        .replace('{startDate}', DateTimeHelper.Format(rb.startDate) ?? '')
                                        .replace('{endDate}', DateTimeHelper.Format(rb.endDate) ?? '')
                                }
                                {` (${rb.totalDates} ${translate('days').toLowerCase()})`}
                            </Typography>
                        </Box>

                        <Box className='box-information'>
                            <Block />
                            <Typography variant='subtitle2'>
                                {rb.typeDescription}
                            </Typography>
                        </Box>

                        <Box className='box-information'>
                            <Today />
                            <Typography variant='subtitle2'>
                                {translate('createdAt')}
                                {' '}
                                {
                                    translate('dateAtTime')
                                        .replace('{date}', DateTimeHelper.Format(rb.createdAt) ?? '')
                                        .replace('{time}', DateTimeHelper.Format(rb.createdAt, 'HH:mm') ?? '')
                                }
                            </Typography>
                        </Box>

                        <Box className='box-information'>
                            <People />
                            <Typography variant='subtitle2'>
                                {translate('createdBy')}
                                {' '}
                                {rb.createdBy}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Grid>;
        });
        // eslint-disable-next-line
    }, [state.values.data, state.values, search.value]);


    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        hasPermission(`${linkProps.alias[0]}.Add`) && (
                            <Tooltip title={translate('add')}>
                                <IconButton onClick={() => handleAddOrView(undefined)}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    <Tooltip title={translate('search')}>
                        <IconButton onClick={() => search.handleOpen()}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            {
                roomBlockings.length > 0 ? (
                    <Grid 
                        container 
                        className='grid-content overflow-enabled justifycontent-flexstart aligncontent-flexstart' 
                        rowSpacing={DefaultConstants.gridRowSpacing} 
                        columnSpacing={DefaultConstants.gridColumnSpacing}
                    >
                        {roomBlockings}
                    </Grid>
                ) : (
                    <Grid container className='grid-content'>
                        <Grid item xs={12} className='paper-content display-flex alignitems-center justifycontent-center background-transparent boxshadow-none'>
                            <Empty text='noRoomBlockingsFound' className="backgroundcolor-paper boxshadow-default" />
                        </Grid>
                    </Grid>
                )
            }

            <Search
                open={search.open}
                handleClose={() => search.handleClose()}
                value={search.value}
                handleChange={(e) => search.handleChangeValue(e.currentTarget.value)}
            />

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                handleApplyFilters={(formData) => state.update('filters', formData)}
            />

            <AddDialog
                open={addDialog.open}
                handleClose={() => addDialog.handleClose()}
                data={state.values.selectedRoomBlocking}
                handleListRefresh={handleRefresh}
            />

            <ViewDialog
                open={viewDialog.open}
                handleClose={() => viewDialog.handleClose()}
                data={state.values.selectedRoomBlocking}
                handleListRefresh={handleRefresh}
                hasPermissionToRemove={hasPermission(`${linkProps.alias[0]}.Remove`)}
            />
        </>
    );
}

export default RoomBlocking;